import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  MessageCircle, 
  Send, 
  Search, 
  Smile,
  MoreVertical,
  Clock,
  Calendar,
  CheckCircle
} from 'lucide-react';
import DataFetchState from '../components/ui/DataFetchState';

const Messages = () => {
  const {
    messages,
    bookings,
    sendMessage,
    userDataLoading,
    userDataError,
    refreshUserData,
  } = useData();
  const { user } = useAuth();
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const [showEmoji, setShowEmoji] = useState(false);

  const userId = user?.uid || user?.id;
  const isCaregiver = user?.role === 'caregiver';

  const emptyInboxCopy = isCaregiver
    ? {
        hint: 'When a pet owner sends a booking request or you accept a job, you can message them here.',
        cta: { label: 'View booking requests', to: '/caregiver-bookings' },
      }
    : {
        hint: 'Messages appear after you book a caregiver.',
        cta: { label: 'Find a caregiver', to: '/search' },
      };

  // Get conversations for the current user
  const conversations = bookings
    .filter(booking => booking.ownerId === userId || booking.caregiverId === userId)
    .map(booking => {
      const otherUserId = booking.ownerId === userId ? booking.caregiverId : booking.ownerId;
      const otherUser = booking.ownerId === userId ? booking.caregiver : booking.owner;
      const bookingMessages = messages.filter(msg => msg.bookingId === booking.id);
      const lastMessage = bookingMessages[bookingMessages.length - 1];
      
      return {
        ...booking,
        otherUserId,
        otherUser,
        messages: bookingMessages,
        lastMessage,
        unreadCount: bookingMessages.filter(msg => 
          msg.fromUserId !== userId && !msg.read
        ).length
      };
    })
    .sort((a, b) => {
      const aTime = a.lastMessage?.createdAt || a.createdAt;
      const bTime = b.lastMessage?.createdAt || b.createdAt;
      return new Date(bTime) - new Date(aTime);
    });

  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations;
    const q = searchTerm.trim().toLowerCase();
    return conversations.filter((c) => {
      const name = c.otherUser?.name || '';
      const pet = c.pet?.name || c.petName || '';
      const service = c.serviceName || c.service || '';
      return (
        name.toLowerCase().includes(q) ||
        pet.toLowerCase().includes(q) ||
        service.toLowerCase().includes(q)
      );
    });
  }, [conversations, searchTerm]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedBooking?.messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedBooking) return;

    sendMessage({
      bookingId: selectedBooking.id,
      fromUserId: userId,
      toUserId: selectedBooking.otherUserId,
      text: newMessage.trim()
    });

    setNewMessage('');
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="pf-page-wrap pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <DataFetchState
          loading={userDataLoading}
          error={userDataError}
          onRetry={refreshUserData}
          loadingLabel="Loading conversations"
          errorTitle="Couldn't load messages"
          compact
        >
        <div className="h-[calc(100vh-8rem)] lg:h-[calc(100vh-9rem)] bg-[var(--pf-surface)] rounded-2xl shadow-sm border border-[var(--pf-border)] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 h-full">
            {/* Conversations List */}
            <div className="lg:border-r border-gray-200">
              {/* Search Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative input-icon-wrap">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input"
                    placeholder="Search conversations..."
                  />
                </div>
              </div>

              {/* Conversations */}
              <div className="overflow-y-auto h-[calc(100%-4.5rem)]">
                {filteredConversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageCircle className="w-12 h-12 text-[var(--pf-ink-muted)] mx-auto mb-3 opacity-40" />
                    <p className="font-medium text-[var(--pf-ink)]">
                      {conversations.length === 0 ? 'No conversations yet' : 'No matches found'}
                    </p>
                    <p className="text-sm text-[var(--pf-ink-muted)] mt-1 mb-4">
                      {conversations.length === 0
                        ? emptyInboxCopy.hint
                        : 'Try a different search term'}
                    </p>
                    {conversations.length === 0 && (
                      <Link to={emptyInboxCopy.cta.to} className="pf-btn-primary inline-flex text-sm">
                        {emptyInboxCopy.cta.label}
                      </Link>
                    )}
                  </div>
                ) : (
                filteredConversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    onClick={() => setSelectedBooking(conversation)}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition-colors ${
                      selectedBooking?.id === conversation.id ? 'bg-[var(--pf-accent-soft)]' : ''
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={conversation.otherUser?.avatar}
                        alt={conversation.otherUser?.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      {conversation.unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--pf-accent)] text-white text-xs rounded-full flex items-center justify-center">
                          {conversation.unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-900 truncate">
                          {conversation.otherUser?.name}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {conversation.lastMessage 
                            ? formatTime(conversation.lastMessage.createdAt)
                            : formatDate(conversation.createdAt)
                          }
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {conversation.pet?.name} • {conversation.status}
                      </p>
                      {conversation.lastMessage && (
                        <p className="text-sm text-gray-500 truncate mt-1">
                          {conversation.lastMessage.text}
                        </p>
                      )}
                    </div>
                  </button>
                ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-2 flex flex-col h-full">
              {selectedBooking ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src={selectedBooking.otherUser?.avatar}
                        alt={selectedBooking.otherUser?.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {selectedBooking.otherUser?.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span>{selectedBooking.pet?.name}</span>
                          <span className={`w-2 h-2 rounded-full ${
                            selectedBooking.status === 'completed' ? 'bg-green-500' :
                            selectedBooking.status === 'in_progress' ? 'bg-blue-500' :
                            'bg-gray-500'
                          }`} />
                          <span className="capitalize">{selectedBooking.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                        aria-label="More options"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {selectedBooking.messages.length > 0 ? (
                      selectedBooking.messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.fromUserId === userId ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] ${message.fromUserId === userId ? 'order-2' : 'order-1'}`}>
                            <div className={`rounded-2xl px-4 py-2 ${
                              message.fromUserId === userId
                                ? 'bg-[var(--pf-accent)] text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}>
                              <p className="text-sm">{message.text}</p>
                            </div>
                            <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                              message.fromUserId === userId ? 'justify-end' : 'justify-start'
                            }`}>
                              {formatTime(message.createdAt)}
                              {message.fromUserId === userId && (
                                <CheckCircle className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-center">
                        <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
                        <p className="text-gray-500 mb-2">No messages yet</p>
                        <p className="text-sm text-gray-400">
                          Start the conversation with {selectedBooking.otherUser?.name}
                        </p>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200">
                    <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                      <button
                        type="button"
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                        onClick={() => setShowEmoji(!showEmoji)}
                        aria-label="Insert emoji"
                      >
                        <Smile className="w-5 h-5" />
                      </button>
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="flex-1 form-input"
                        placeholder="Type your message..."
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="btn-primary p-2 rounded-xl disabled:opacity-50"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <MessageCircle className="w-20 h-20 text-gray-300 mb-6" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {filteredConversations.length === 0 && conversations.length === 0
                      ? 'Your inbox is empty'
                      : 'Select a conversation'}
                  </h3>
                  <p className="text-gray-500 max-w-sm">
                    {filteredConversations.length === 0 && conversations.length === 0
                      ? emptyInboxCopy.hint
                      : 'Choose a conversation from the list to start messaging'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        </DataFetchState>
      </div>
    </div>
  );
};

export default Messages;